import { RootState } from '../../store';
import { getPlatformUserId } from '../../../utils/utils';
import { isInProgressStatus } from './constants';

export const selectSelectedConversation = (state: RootState) => {
    const { conversations, selectedConversationId } = state.dashboard.askAicaReducer;
    if (!selectedConversationId) return null;
    return conversations.find((c) => c.conversation_id === selectedConversationId) ?? null;
};

export const selectViewOnly = (state: RootState): boolean => {
    const { selectedConversationId } = state.dashboard.askAicaReducer;
    if (!selectedConversationId) return false;
    const selectedRow = selectSelectedConversation(state);
    if (!selectedRow) return false;
    return selectedRow.user_id !== getPlatformUserId();
};

/** The minimum a message must carry for the newest-turn derivation below —
 * satisfied by both surfaces' message types. */
interface TurnMessage {
    id: number | string;
    type: 'user' | 'assistant';
    status?: string;
    metadata?: { user_message_id?: string };
}

export interface NewestTurn {
    /** The run handle `/chat/stop` and the re-attach stream both take. Undefined
     * when the turn predates this feature or the thread holds only optimistic ids. */
    userMessageId?: string;
    /** The assistant row id the re-attach path keys `updateMessage` on. */
    assistantMessageId?: number | string;
    /** The answer is still being produced server-side. */
    isInProgress: boolean;
}

/**
 * Reads the newest turn's run state off its two messages.
 *
 * ⚠️ The CALLER supplies the pair, because the two surfaces store their threads
 * in opposite orders: leads is newest-first (its history mapper reverses items
 * and the reducer appends older pages onto the tail), portfolio is oldest-first
 * (its mapper sorts ascending). Deriving the order in here would be wrong on one
 * of them.
 *
 * The run handle has two sources and needs both: a LIVE turn carries it in the
 * assistant message's metadata (stamped from the first SSE chunk), while a turn
 * loaded from history has it as the user message's own server id.
 */
export const deriveNewestTurn = (newestAssistant?: TurnMessage, newestUser?: TurnMessage): NewestTurn => {
    if (!newestAssistant || newestAssistant.type !== 'assistant') return { isInProgress: false };

    const historyUserMessageId = newestUser?.type === 'user' && typeof newestUser.id === 'string' ? newestUser.id : undefined;

    return {
        userMessageId: newestAssistant.metadata?.user_message_id ?? historyUserMessageId,
        assistantMessageId: newestAssistant.id,
        isInProgress: isInProgressStatus(newestAssistant.status),
    };
};
